import React, { useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import PropTypes from 'prop-types';
import styled from 'styled-components';

/**
 * @param {Object_Array} items // { _id, type, value, depth, loadRequired }
 */
const CollectionRenderer = ({ items, itemRenderer, virtual, startIndex, endIndex }) => {

  /**
   * Step 1: Handle which items should be rendered
   */
  let itemsToRender = items;
  if (typeof startIndex === 'number' && typeof endIndex === 'number') {
    itemsToRender = items.slice(startIndex, endIndex);
  }

  /**
   * Step 2: Select renderer
   */
  const Renderer = virtual ? VirtualizedRenderer : StandardRenderer;

  /**
   * Step 3: Handle measurement of rendered items
   */
  const handleLoadCompleted = (rowIndex) => {};

  return (
    <Renderer {...{ items: itemsToRender, itemRenderer, onLoadCompleted: handleLoadCompleted }} />
  );
};

const StandardRenderer = ({ items, itemRenderer, onLoadCompleted }) => {

  return (
    <div>
      {items.map((item, index) => itemRenderer({ item, index, onLoadCompleted, items }))}
    </div>
  );

};

const VirtualizedStyled = styled.div`
  height: 100%;
  overflow: auto;
  scrollbar-gutter: stable;
`;

const VirtualizedRenderer = ({ items, itemRenderer }) => {

  const parentRef = useRef();

  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 45,
  });

  return (
    <VirtualizedStyled ref={parentRef}>
      <div
        style={{
          height: rowVirtualizer.getTotalSize(),
          width: '100%',
          position: 'relative',
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={rowVirtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {itemRenderer({ item: items[virtualRow.index], index: virtualRow.index, items })}
            </div>
          );

        })}
      </div>
    </VirtualizedStyled>
  );
};

export default CollectionRenderer;
